// src/utils/soundEngine.ts
// Pure WebAudio API synthesized acoustic micro-feedback engine for Corpix OS
// Zero external MP3/WAV files needed. Synthesizes cyber-acoustic waveforms in real-time.

import { useStore } from '@/hooks/useStore'

let audioCtx: AudioContext | null = null

const getContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (AudioContextClass) {
      audioCtx = new AudioContextClass()
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {})
  }
  return audioCtx
}

export const soundEngine = {
  // 1. Crisp, subtle high-frequency micro-click when hovering over cards or nav items
  playHover: () => {
    if (!useStore.getState().soundEnabled) return
    const ctx = getContext()
    if (!ctx) return

    try {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.type = 'sine'
      // Rapid sweep from 1600Hz down to 800Hz over 35ms
      osc.frequency.setValueAtTime(1600, ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.035)

      gain.gain.setValueAtTime(0.04, ctx.currentTime) // Very subtle, pleasant volume
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.035)

      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.035)
    } catch {
      // Ignore audio context errors if browser blocked autoplay
    }
  },

  // 2. Futuristic dual-tone holographic chime when clicking a case study card or filter tab
  playSelect: () => {
    if (!useStore.getState().soundEnabled) return
    const ctx = getContext()
    if (!ctx) return

    try {
      // Tone 1: Fundamental 580Hz
      const osc1 = ctx.createOscillator()
      const gain1 = ctx.createGain()
      osc1.type = 'triangle'
      osc1.frequency.setValueAtTime(580, ctx.currentTime)
      osc1.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.12)

      gain1.gain.setValueAtTime(0.06, ctx.currentTime)
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12)

      osc1.connect(gain1)
      gain1.connect(ctx.destination)
      osc1.start(ctx.currentTime)
      osc1.stop(ctx.currentTime + 0.12)

      // Tone 2: Harmonic Overtone 1160Hz (starts 20ms later)
      const osc2 = ctx.createOscillator()
      const gain2 = ctx.createGain()
      osc2.type = 'sine'
      osc2.frequency.setValueAtTime(1160, ctx.currentTime + 0.02)
      osc2.frequency.exponentialRampToValueAtTime(1440, ctx.currentTime + 0.15)

      gain2.gain.setValueAtTime(0.03, ctx.currentTime + 0.02)
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15)

      osc2.connect(gain2)
      gain2.connect(ctx.destination)
      osc2.start(ctx.currentTime + 0.02)
      osc2.stop(ctx.currentTime + 0.15)
    } catch {
      // Ignore
    }
  },

  // 3. Deep cyber-boot sweep when launching interactive sandbox or switching sections
  playLaunch: () => {
    if (!useStore.getState().soundEnabled) return
    const ctx = getContext()
    if (!ctx) return

    try {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.type = 'sawtooth'
      osc.frequency.setValueAtTime(140, ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(720, ctx.currentTime + 0.28)

      gain.gain.setValueAtTime(0.05, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.28)

      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.28)
    } catch {
      // Ignore
    }
  },

  // 4. Subtle spatial sci-fi warp whoosh when triggering 3D warp across sections
  playWarp: () => {
    if (!useStore.getState().soundEnabled) return
    const ctx = getContext()
    if (!ctx) return

    try {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.type = 'sine'
      osc.frequency.setValueAtTime(320, ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(65, ctx.currentTime + 0.35)

      gain.gain.setValueAtTime(0.08, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35)

      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.35)
    } catch {
      // Ignore
    }
  },
}
